// src/pages/Auth/Register.jsx
import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Slide,
} from '@mui/material';
import {
  Business,
  Person,
  Security,
  CheckCircle,
  Visibility,
  VisibilityOff,
  Email,
  Phone,
  LocationOn,
  AccountBalance,
  ArrowBack,
  ArrowForward,
  Verified,
  Shield,
  Speed,
  Support,
  TrendingUp,
  PersonAdd,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import AuthService from '../../services/authService';

// Brand Config
import { BRAND_CONFIG } from '../../config/brandConfig';

// Validation schema
const getValidationSchema = (step) => {
  const schemas = {
    0: yup.object({
      companyName: yup.string().required('Company name is required').min(2, 'At least 2 characters'),
      taxNumber: yup.string().required('Tax number is required').length(10, 'Tax number must be 10 digits'),
      companyType: yup.string().required('Please select company type'),
      businessArea: yup.string().required('Please select business area'),
      address: yup.string().required('Address is required').min(10, 'At least 10 characters'),
      city: yup.string().required('Please select city'),
    }),
    1: yup.object({
      name: yup.string().required('First name is required').min(2, 'At least 2 characters'),
      surname: yup.string().required('Last name is required').min(2, 'At least 2 characters'),
      title: yup.string().required('Title is required'),
      email: yup.string().required('Email is required').email('Please enter a valid email address'),
      phone: yup.string().required('Phone is required').matches(/^[0-9]{10}$/, 'Please enter a valid phone number'),
    }),
    2: yup.object({
      password: yup.string()
        .required('Password is required')
        .min(8, 'At least 8 characters')
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Must contain at least one uppercase letter, lowercase letter, and number'),
      confirmPassword: yup.string()
        .required('Password confirmation is required')
        .oneOf([yup.ref('password')], 'Passwords do not match'),
      termsAccepted: yup.boolean().oneOf([true], 'You must accept the terms of use'),
    }),
  };
  return schemas[step];
};

const steps = [
  { label: 'Company Information', icon: <Business /> },
  { label: 'Authorized Person Information', icon: <Person /> },
  { label: 'Account Security', icon: <Security /> }
];

const companyTypes = [
  { value: 'Joint Stock Company', label: 'Joint Stock Company (JSC)', popular: true },
  { value: 'Limited Company', label: 'Limited Company (Ltd.)', popular: true },
  { value: 'Collective Company', label: 'Collective Company', popular: false },
  { value: 'Commandite Company', label: 'Commandite Company', popular: false },
  { value: 'Individual Company', label: 'Individual Company', popular: false },
];

const businessAreas = [
  { value: 'Technology', label: 'Technology & Software', popular: true },
  { value: 'Manufacturing', label: 'Manufacturing & Production', popular: true },
  { value: 'Trade', label: 'Trade & Distribution', popular: true },
  { value: 'Services', label: 'Service Sector', popular: true },
  { value: 'Construction', label: 'Construction & Real Estate', popular: false },
  { value: 'Food', label: 'Food & Beverage', popular: false },
  { value: 'Textile', label: 'Textile & Apparel', popular: false },
  { value: 'Automotive', label: 'Automotive & Transportation', popular: false },
  { value: 'Healthcare', label: 'Healthcare & Medical Services', popular: false },
  { value: 'Education', label: 'Education & Consulting', popular: false },
  { value: 'Tourism', label: 'Tourism & Hospitality', popular: false },
  { value: 'Other', label: 'Other', popular: false },
];

const cities = [
  'İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Antalya', 'Adana', 'Konya', 'Gaziantep',
  'Şanlıurfa', 'Kocaeli', 'Mersin', 'Diyarbakır', 'Hatay', 'Manisa', 'Kayseri',
  'Samsun', 'Balıkesir', 'Kahramanmaraş', 'Van', 'Aydın', 'Denizli', 'Sakarya'
];
const roles = [
  { 
    value: 'Buyer', 
    label: 'Buyer Account', 
    description: 'I will request credit',
    color: 'primary',
    icon: <TrendingUp />,
    features: ['Create credit requests', 'View limits', 'Track transactions']
  },
  { 
    value: 'Seller', 
    label: 'Seller Account', 
    description: 'I will provide credit/guarantee',
    color: 'success',
    icon: <AccountBalance />,
    features: ['Provide guarantees', 'Approve requests', 'Credit analysis']
  },
];

const features = [
  { title: 'Banking Level Security', description: 'Protected transactions with SSL encryption', icon: <Shield /> },
  { title: 'Fast Transaction Process', description: 'Transaction approval in minutes', icon: <Speed /> },
  { title: '24/7 Support', description: 'Uninterrupted customer service', icon: <Support /> },
];

const Register = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState({});

  const { control, handleSubmit, formState: { errors }, watch, trigger } = useForm({
    resolver: yupResolver(getValidationSchema(activeStep)),
    mode: 'onChange',
    defaultValues: {
      companyName: '',
      taxNumber: '',
      companyType: '',
      businessArea: '',
      address: '',
      city: '',
      name: '',
      surname: '',
      title: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      termsAccepted: false,
    }
  });

  const watchedValues = watch();

  const handleNext = async () => {
    const isValid = await trigger();
    if (isValid) {
      setRegistrationData({ ...registrationData, ...watchedValues });
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    
    const finalData = { ...registrationData, ...data };
    
    try {
      const result = await AuthService.register(finalData);

      if (result.success) {
        if (window.showToast) {
          window.showToast('Registration completed successfully. Please sign in.', 'success');
        }
        navigate('/login', { 
          state: { 
            message: 'Your registration has been completed successfully! You will be able to sign in after your account is reviewed.' 
          }
        });
      } else {
        if (window.showToast) {
          window.showToast(result.message || 'Registration failed. Please check your information.', 'error');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (window.showToast) {
        window.showToast('Server error. Please try again later.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Fade in={true} timeout={500}>
            <Box>
              
              <Controller
                name="companyName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Company Name"
                    placeholder="Ex: ABC Technology Ltd."
                    error={!!errors.companyName}
                    helperText={errors.companyName?.message}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color={field.value ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
  <Box sx={{ flex: 1 }}>
              <Controller
                name="taxNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Tax Number"
                    placeholder="1234567890"
                    error={!!errors.taxNumber}
                    helperText={errors.taxNumber?.message || "Enter your 10-digit tax number"}
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Verified color={field.value?.length === 10 ? 'success' : 'disabled'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
  </Box>
  <Box sx={{ flex: 1 }}>
              <Controller
                name="companyType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.companyType}>
                    <InputLabel>Company Type</InputLabel>
                    <Select {...field} label="Company Type">
                      {companyTypes.filter(type => type.popular).map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <span>{type.label}</span>
                            <Chip label="Popular" size="small" color="primary" variant="outlined" />
                          </Box>
                        </MenuItem>
                      ))}
                      <Divider />
                      {companyTypes.filter(type => !type.popular).map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.companyType?.message}</FormHelperText>
                  </FormControl>
                )}
              />
   </Box>
</Box>
<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
  <Box sx={{ flex: 1 }}>
              <Controller
                name="businessArea"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.businessArea} sx={{ mb: 3 }}>
                    <InputLabel>Business Area</InputLabel>
                    <Select {...field} label="Business Area">
                      {businessAreas.filter(area => area.popular).map((area) => (
                        <MenuItem key={area.value} value={area.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{area.label}</span>
                            <Chip label="Popular" size="small" color="primary" variant="outlined" />
                          </Box>
                        </MenuItem>
                      ))}
                      <Divider />
                      {businessAreas.filter(area => !area.popular).map((area) => (
                        <MenuItem key={area.value} value={area.value}>
                          <span>{area.label}</span>
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.businessArea?.message}</FormHelperText>
                  </FormControl>
                )}
              />
</Box>
  <Box sx={{ flex: 1 }}>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.city} sx={{ mb: 3 }}>
                    <InputLabel>City</InputLabel>
                    <Select {...field} label="City">
                      {cities.map((city) => (
                        <MenuItem key={city} value={city}>
                          {city}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors.city?.message}</FormHelperText>
                  </FormControl>
                )}
              />
</Box></Box>
              <Controller
                name="address"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={3}
                    label="Company Address"
                    placeholder="Neighborhood, Street, Avenue, Building No, Floor, Apartment..."
                    error={!!errors.address}
                    helperText={errors.address?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                          <LocationOn color={field.value ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Fade>
        );

      case 1:
        return (
          <Slide direction="left" in={true} timeout={500}>
            <Box>
<Box sx={{ display: 'flex', gap: 2 }}>
  <Box sx={{ flex: 1 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="First Name"
                    placeholder="Enter your first name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    sx={{ mb: 3 }}
                  />
                )}
              />
              </Box>
<Box sx={{ flex: 1 }}>
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Last Name"
                    placeholder="Enter your last name"
                    error={!!errors.surname}
                    helperText={errors.surname?.message}
                    sx={{ mb: 3 }}
                  />
                )}
              />
</Box></Box>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Title"
                    placeholder="General Manager, Finance Manager, CEO..."
                    error={!!errors.title}
                    helperText={errors.title?.message || ""}
                    sx={{ mb: 3 }}
                  />
                )}
              />
<Box sx={{ display: 'flex', gap: 2 }}>
  <Box sx={{ flex: 1 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email Address"
                    type="email"
                    placeholder="example@yourcompany.com"
                    error={!!errors.email}
                    helperText={errors.email?.message || ""}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color={field.value?.includes('@') ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              </Box>
  <Box sx={{ flex: 1 }}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    placeholder="5XXXXXXXXX"
                    error={!!errors.phone}
                    helperText={errors.phone?.message || ""}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color={field.value?.length === 10 ? 'success' : 'disabled'} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
</Box></Box>
              
              {/* Role Information - Visual Only */}
              <Box sx={{ mb: 3, mt: 1 }}>
                <Grid container spacing={2}>
                  {roles.map((role) => (
                    <Grid item xs={12} md={6} key={role.value}>
                      <Card 
                        variant="outlined"
                        sx={{ 
                          transition: 'all 0.3s ease',
                          border: 1,
                          borderColor: `${role.color}.main`,
                          backgroundColor: 'white', 
                          opacity: 0.9,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 2,
                            opacity: 1,
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ 
                                px: 1,
                                py:0.5,
                                borderRadius: 2, 
                                backgroundColor: `${role.color}.main`,
                                color: 'white'
                              }}>
                                {role.icon}
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: `${role.color}.main` }}>
                                {role.label}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {role.description}
                          </Typography>
                          <List dense>
                            {role.features.map((feature, index) => (
                              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <CheckCircle fontSize="small" color={role.color} />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={feature}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Slide>
        );


      case 2:
        return (
          <Fade in={true} timeout={500}>
            <Grid container spacing={2}>
              
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      error={!!errors.password}
                      helperText={errors.password?.message || 'At least 8 characters, uppercase, lowercase and number'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Confirm Password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Important Information:</strong> After your registration is completed, your account 
                    will be taken under review for security checks and will become active within 24 hours.
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="termsAccepted"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        p: 2, 
                        backgroundColor: 'grey.50', 
                        borderRadius: 2,
                        border: errors.termsAccepted ? '1px solid' : 'none',
                        borderColor: 'error.main'
                      }}>
                        <input 
                          type="checkbox" 
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          style={{ 
                            marginRight: 12, 
                            marginTop: 2,
                            transform: 'scale(1.2)'
                          }}
                        />
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          I have read, understood and accept the <strong>{BRAND_CONFIG.appName} Terms of Use</strong> and <strong>Privacy Policy</strong>. 
                          I consent to the processing of my personal data and to be contacted.
                        </Typography>
                      </Box>
                      {errors.termsAccepted && (
                        <FormHelperText error sx={{ ml: 1 }}>
                          {errors.termsAccepted.message}
                        </FormHelperText>
                      )}
                    </Box>
                  )}
                />
              </Grid>
            </Grid>
          </Fade>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(16, 38, 166) 0%, rgba(14, 33, 148) 50%, rgba(12, 28, 130) 100%)',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          
          {/* Left Side - Branding & Features */}
          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' }, color: 'white' }}>
            {/* Logo & Branding */}
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img 
                    src={BRAND_CONFIG.logo} 
                    alt={BRAND_CONFIG.name}
                    style={{ 
                      width: '56px', 
                      height: '56px', 
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {BRAND_CONFIG.name}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300 }}>
                    {BRAND_CONFIG.tagline}
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
                Corporate Membership System
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                Join the {BRAND_CONFIG.appName} family for secure, fast and professional financial solutions. 
                Credit requests, guarantee transactions and limit management all in one platform.
              </Typography>
            </Box>

            {/* Features */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {features.map((feature, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    flexShrink: 0
                  }}>
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.5 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Side - Registration Form */}
          <Box sx={{ flex: 1, maxWidth: 600 }}>
            <Paper elevation={24} sx={{ p: 5, borderRadius: 4 }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <PersonAdd sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Company Registration
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Register your company in the {BRAND_CONFIG.appName} system
                </Typography>
              </Box>

              {/* Enhanced Stepper */}
              <Box sx={{ mb: 4 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel 
                        icon={
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: activeStep >= index ? 'primary.main' : 'grey.300',
                            color: 'white'
                          }}>
                            {activeStep > index ? <CheckCircle /> : step.icon}
                          </Box>
                        }
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {step.label}
                          </Typography>
                        </Box>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <LinearProgress 
                  variant="determinate" 
                  value={(activeStep / (steps.length - 1)) * 100} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Form Content */}
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                {getStepContent(activeStep)}

                {/* Navigation Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>

                  <Button
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                    variant="outlined"
                    sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
                  >
                    Back
                  </Button>

                  <Box sx={{ flexGrow: 1 }} />

                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{ 
                        minWidth: 160,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #00c853 0%, #00a726 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #00a726 0%, #007c1f 100%)',
                        }
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress sx={{ width: 20, height: 2 }} />
                          Saving...
                        </Box>
                      ) : (
                        <>
                          <CheckCircle sx={{ mr: 1 }} />
                          Complete Registration
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      endIcon={<ArrowForward />}
                      size="large"
                      sx={{ 
                        minWidth: 120, 
                        py: 1.5,
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                        }
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Footer */}
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  or
                </Typography>
              </Divider>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?
                </Typography>
                <Button 
                  variant="text"
                  onClick={() => navigate('/login')}
                   sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', p: 0.5 }}
                >
                  Sign In
                </Button>                
              </Box>

              {loading && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress />
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                    Your account is being created, please wait...
                  </Typography>
                </Box>
              )}

            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;